﻿import {translation} from "@2d-front-monorepo/core";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {JSX} from "react";
import FeeDisplayLayout from "../enums/FeeDisplayLayout";
import {formatCurrency} from "../helpers/formatCurrency";
import {formatPercentage} from "../helpers/formatPercentage";
import {PriceBreakdown} from "../types/Price";

export enum ParkingFeeDisplayType {
	Summary,
	Unparking,
}

export interface ParkingFeeDisplayProps {
	price: PriceBreakdown | undefined;
	layout: FeeDisplayLayout | undefined;
	autoHide?: boolean;
	type?: ParkingFeeDisplayType;
}

interface PriceBreakdownProps extends PriceBreakdown {
	type: ParkingFeeDisplayType;
}

function postfixTranslation(key: string, type: ParkingFeeDisplayType): string {
	switch (type) {
		case ParkingFeeDisplayType.Unparking:
			return key + "--unparking";

		default:
			return key;
	}
}

function feeLayoutStyles(display: FeeDisplayLayout): string {
	switch (display) {
		case FeeDisplayLayout.DetailFee:
			return "detail-fee";
		case FeeDisplayLayout.SummaryFee:
			return "summary-fee";
		case FeeDisplayLayout.Bonus:
			return "bonus";
		case FeeDisplayLayout.FeeWithVat:
			return "fee-with-vat";
		case FeeDisplayLayout.VatFeePlus:
			return "vat-fee-plus";
		case FeeDisplayLayout.Bonification:
			return "bonification";
		case FeeDisplayLayout.BSM:
			return "bsm";
		case FeeDisplayLayout.FeeWithTwoVats:
			return "fee-with-two-vats";
		default:
			return "no-fee";
	}
}

function Total(props: PriceBreakdownProps): JSX.Element {
	const {total, wallet, type} = props;
	return (<dl className="title-desc">
		<dt>{translation(postfixTranslation(PermitTranslations.total, type))}</dt>
		<dd className={"tabular-nums"}>{
			(wallet === 0 || wallet === total)
				? <>{formatCurrency(total)}</>
				: <><s>{formatCurrency(total)}</s> {formatCurrency(total - wallet)}</>
		}
		</dd>
	</dl>);
}

function NoFee(props: PriceBreakdownProps): JSX.Element {
	const {total, wallet, type} = props;

	return (<>
		<dl className="title-desc">
			<dt>{translation(postfixTranslation(PermitTranslations.total, type))}</dt>
			<dd className={"tabular-nums"}>{
				(wallet === 0 || wallet === total)
					? <>{formatCurrency(total)}</>
					: <><s>{formatCurrency(total)}</s> {formatCurrency(total - wallet)}</>
			}</dd>
		</dl>
	</>);
}

function DetailFee(props: PriceBreakdownProps): JSX.Element {
	const {amount, fee, vat, type} = props;
	return (
		<>
			<dl className="title-desc">
				<dt>{translation(postfixTranslation(PermitTranslations.fee, type))}</dt>
				<dd className={"tabular-nums"}>{formatCurrency(fee)}</dd>
			</dl>
			<dl className="title-desc">
				<dt>{translation(postfixTranslation(PermitTranslations.vat, type))}</dt>
				<dd className={"tabular-nums"}>{formatCurrency(vat)}</dd>
			</dl>
			<dl className="title-desc">
				<dt>{translation(postfixTranslation(PermitTranslations.amount, type))}</dt>
				<dd className={"tabular-nums"}>{formatCurrency(amount)}</dd>
			</dl>
			<Total {...props}/>
		</>
	);
}

function SummaryFee(props: PriceBreakdownProps): JSX.Element {
	const {amount, type} = props;
	return (
		<>
			<dl className="title-desc">
				<dt>{translation(postfixTranslation(PermitTranslations.sub_total, type))}</dt>
				<dd className={"tabular-nums"}>{formatCurrency(amount)}</dd>
			</dl>
			<Total {...props}/>
		</>
	);
}

function FeeWithVat(props: PriceBreakdownProps): JSX.Element {
	const {amount, fee, vat, type} = props;
	return (
		<>
			<dl className="title-desc">
				<dt>{translation(postfixTranslation(PermitTranslations.amount, type))}</dt>
				<dd className={"tabular-nums"}>{formatCurrency(amount)}</dd>
			</dl>
			<dl className="title-desc">
				<dt>{translation(postfixTranslation(PermitTranslations.fee_plus_vat, type))}</dt>
				<dd className={"tabular-nums"}>{formatCurrency(fee + vat)}</dd>
			</dl>
			<Total {...props}/>
		</>
	);
}

function VatFeePlus(props: PriceBreakdownProps): JSX.Element {
	const {amountPlusVat, feePlusVat, type} = props;

	return (
		<>
			<dl className="title-desc">
				<dt>{translation(postfixTranslation(PermitTranslations.amount_plus_vat, type))}</dt>
				<dd className={"tabular-nums"}>{formatCurrency(amountPlusVat)}</dd>
			</dl>
			<dl className="title-desc">
				<dt>{translation(postfixTranslation(PermitTranslations.fee_plus_vat, type))}</dt>
				<dd className={"tabular-nums"}>{formatCurrency(feePlusVat)}</dd>
			</dl>
			<Total {...props}/>
		</>
	);
}

function Bonification(props: PriceBreakdownProps): JSX.Element {
	const {amount, vat, amountWithoutBonification, bonification, feePlusVat, type} = props;
	const bonificationAmount = amount - amountWithoutBonification;
	const bonificationPercent = (bonification * 100) - 100;
	const bonificationType = bonificationPercent <= 0 ? "discount" : "surcharge";

	return (
		<>
			<dl className="title-desc">
				<dt>{translation(postfixTranslation(PermitTranslations.amount, type))}</dt>
				<dd className={"tabular-nums"}>{formatCurrency(amount)}</dd>
			</dl>
			<dl className="title-desc">
				<dt>{translation(postfixTranslation(PermitTranslations.vat, type))}</dt>
				<dd className={"tabular-nums"}>{formatCurrency(vat)}</dd>
			</dl>
			<dl className="title-desc">
				<dt>{translation(postfixTranslation(PermitTranslations.fee_plus_vat, type))}</dt>
				<dd className={"tabular-nums"}>{formatCurrency(feePlusVat)}</dd>
			</dl>

			<dl className="title-desc">
				<dt>{translation(postfixTranslation(PermitTranslations.amount_without_bonification, type))}</dt>
				<dd className={"tabular-nums"}>{formatCurrency(amountWithoutBonification)}</dd>
			</dl>
			<dl className="title-desc">
				<dt>{translation(postfixTranslation(PermitTranslations.bonification_amount, type))}</dt>
				<dd className={"tabular-nums"}>{formatCurrency(bonificationAmount)}</dd>
			</dl>
			<dl className="title-desc">
				<dt>{translation(postfixTranslation(`bonification-${bonificationType}`, type))}</dt>
				<dd className={"tabular-nums"}>{formatPercentage(Math.abs(bonificationPercent))}</dd>
			</dl>
			<Total {...props}/>
		</>
	);
}

function FeeWithTwoVats(props: PriceBreakdownProps): JSX.Element {
	const {subtotal, vatBreakdown, feePlusVat, type} = props;
	const vat1 = vatBreakdown.find(x => x.label === "ServiceVAT0Lbl");
	const vat2 = vatBreakdown.find(x => x.label === "ServiceVAT1Lbl");

	return (
		<>
			<dl className="title-desc">
				<dt>{translation(postfixTranslation(PermitTranslations.sub_total, type))}</dt>
				<dd className={"tabular-nums"}>{formatCurrency(subtotal)}</dd>
			</dl>
			<dl className="title-desc">
				<dt>{translation(postfixTranslation(vat1?.label ?? "", type))}</dt>
				<dd className={"tabular-nums"}>{formatCurrency(vat1?.vatAmount ?? 0)}</dd>
			</dl>
			<dl className="title-desc">
				<dt>{translation(postfixTranslation(vat2?.label ?? "", type))}</dt>
				<dd className={"tabular-nums"}>{formatCurrency(vat2?.vatAmount ?? 0)}</dd>
			</dl>
			<Total {...props}/>
		</>
	);
}

function AmountPlusVat(props: PriceBreakdownProps): JSX.Element {
	const {amount, vat, fee} = props;
	return (
		<>
			<dl className="title-desc">
				<dt>{translation(PermitTranslations.amount)}</dt>
				<dd className={"tabular-nums"}>{formatCurrency(amount)}</dd>
			</dl>
			<dl className="title-desc">
				<dt>{translation(PermitTranslations.vat)}</dt>
				<dd className={"tabular-nums"}>{formatCurrency(vat)}</dd>
			</dl>
			<dl className="title-desc">
				<dt>{translation(PermitTranslations.fee)}</dt>
				<dd className={"tabular-nums"}>{formatCurrency(fee)}</dd>
			</dl>
			<Total {...props}/>
		</>
	);
}

export default function ParkingFeeDisplay(props: ParkingFeeDisplayProps): JSX.Element {
	const {price, layout, autoHide, type} = props;
	const hideAll = layout == null || price == null || ((autoHide ?? true) && price.total === 0);
	if (hideAll) return <></>;

	const final = {...price, type: type ?? ParkingFeeDisplayType.Summary}

	function feeBody(): JSX.Element {
		switch (layout) {
			case FeeDisplayLayout.DetailFee:
				return <DetailFee {...final}/>;

			case FeeDisplayLayout.SummaryFee:
				return <SummaryFee {...final}/>;

			case FeeDisplayLayout.FeeWithVat:
				return <FeeWithVat {...final}/>;

			case FeeDisplayLayout.VatFeePlus:
				return <VatFeePlus {...final}/>;

			case FeeDisplayLayout.Bonification:
				return <Bonification {...final}/>;

			case FeeDisplayLayout.FeeWithTwoVats:
				return <FeeWithTwoVats {...final}/>;

			case FeeDisplayLayout.AmountPlusVat:
				return <AmountPlusVat {...final}/>;

			default:
				return <NoFee {...final}/>;
		}
	}

	return (
		<footer className={"parking-fee-display parking-fee-display--" + feeLayoutStyles(layout)}>
			{feeBody()}
		</footer>
	);
}
